import React, { useMemo, useState, useEffect } from "react";
import { Tables } from "../../../../../components/Elements/Tables/Tables";
import { ReactComponent as EditIcon } from "../../../../../assets/svgs/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../assets/svgs/delete.svg";
import { SearchBar } from "../../../../../components/Elements/Search/SearchBar";
import { ReactComponent as FilterIcon } from "../../../../../assets/svgs/filters-icon.svg";
import { Button } from "antd";
import EditDepartment from "./EditDepartment";
import AddDepartment from "./AddDepartment";
import FilterLocationDeparment from "../FilterLocationDeparment";
import { fetchAllDepartments } from "../../../../../redux/OrganizationSetup/organizationSetupSlice";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedDepartment } from "../../../../../redux/OrganizationSetup/organizationSetupSlice";
import { deleteDepartment } from "../../../../../redux/OrganizationSetup/organizationSetupSlice";
import PopupCard from "../../../../Auth/PopupCard";
import { EnvironmentOutlined } from "@ant-design/icons";
import LinkLocationPopup from "./LinkLocationPopup";

const Department = ({ onOpenImportDepartment }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [addDepartments, setAddDepartments] = useState(false);
  const [openEditBox, setOpenEditBox] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [openLinkPopup, setOpenLinkPopup] = useState(false);
  const [selectedDeptForLink, setSelectedDeptForLink] = useState(null);

  // Senior dev note: Managing search and filter state locally for immediate feedback
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    departmentId: null,
    departmentName: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    title: "",
    message: "",
  });
  const { orgMode } = useSelector((state) => state.orgSetup);
  const user = useSelector((state) => state.auth.user);
  const effectiveMode = orgMode || user?.orgStructureMode;
  const isLocationFirst = effectiveMode === "location-first";

  const pageSize = 5;
  const dispatch = useDispatch();

  //----API CALL---
  useEffect(() => {
    dispatch(fetchAllDepartments());
  }, [dispatch]);

  //---REDUX STATE---
  const { departmentList } = useSelector((state) => state.orgSetup);

  const filteredDepartmentList = useMemo(() => {
    if (!departmentList) return [];

    return departmentList.filter((dept) => {
      // 1. Apply Search Filter (matches name, type, or status)
      const searchMatch =
        !searchQuery ||
        dept.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dept.department_type
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (dept.isActive ? "active" : "inactive").includes(
          searchQuery.toLowerCase(),
        );

      // 2. Apply Status Filter — treat 'All' as no-filter
      const statusMatch =
        !statusFilter ||
        statusFilter === "All" ||
        (statusFilter === "active" ? dept.isActive : !dept.isActive);

      return searchMatch && statusMatch;
    });
  }, [departmentList, searchQuery, statusFilter]);

  // Static Department Data
  const tableData = useMemo(() => {
    return filteredDepartmentList.map((dept, index) => {
      const row = {
        key: dept._id,
        serial: <div className="text-center">{index + 1}</div>,
        departmentName: <div className="text-center">{dept.name}</div>,
        // departmentType: (
        //   <div className="text-center">{dept.department_type || "-"}</div>
        // ),
        status: (
          <div className="text-center">
            {dept.isActive ? "Active" : "Inactive"}
          </div>
        ),
        action: (
          <div className="flex gap-4 justify-center">
            {!isLocationFirst && (
              <EnvironmentOutlined
                className="text-[16px] text-[#06513D] cursor-pointer"
                onClick={() => {
                  setSelectedDeptForLink(dept);
                  setOpenLinkPopup(true);
                }}
              />
            )}

            <EditIcon
              onClick={() => {
                dispatch(setSelectedDepartment(dept));
                setOpenEditBox(true);
              }}
            />

            <DeleteIcon
              onClick={() =>
                setDeleteConfirmation({
                  visible: true,
                  departmentId: dept._id,
                  departmentName: dept.name,
                })
              }
            />
          </div>
        ),
      };

      //  add location only if needed
      if (!isLocationFirst) {
        row.departmentLocation = (
          <div className="text-center">{dept.linkedLocationCount}</div>
        );
      }

      return row;
    });
  }, [filteredDepartmentList, dispatch, isLocationFirst]);

  // Table Headers
  const headers = useMemo(() => {
    const baseHeaders = [
      { label: "Sr. No.", key: "serial" },
      { label: "Department Name", key: "departmentName" },
      // { label: "Type", key: "departmentType" },
    ];

    if (!isLocationFirst) {
      baseHeaders.push({ label: "Location", key: "departmentLocation" });
    }

    baseHeaders.push(
      { label: "Status", key: "status" },
      { label: "Action", key: "action" },
    );

    return baseHeaders;
  }, [isLocationFirst]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <h1 className="text-[18px] font-bold text-[#06513D] mb-4">
        Add Department
      </h1>

      <div className="bg-white border border-[#3BA2B959] rounded-xl p-4 shadow-md">
        {/* Top Controls */}
        <div className="flex flex-col lg:flex-row lg:justify-between gap-4 px-4 sm:px-6 mb-4">
          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full lg:w-auto">
            <div className="w-full sm:w-auto flex-1">
              <SearchBar
                width="100%"
                onSearch={(value) => setSearchQuery(value)}
                placeholder="Search by name, type or status..."
              />
            </div>

            <Button
              style={{ background: "#E6EDEB", border: "solid 1px #06513D" }}
              className="text-[12px] text-[#06513D] font-light px-4 py-5 hover:!bg-[#F8F8F8] hover:!text-[#06513D]"
              onClick={() => setOpenFilter(true)}
            >
              <FilterIcon className="mr-1" />
              FILTER
            </Button>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full lg:w-auto">
            <Button
              onClick={onOpenImportDepartment}
              style={{ background: "#E6EDEB", border: "solid 1px #06513D" }}
              className="text-[12px] text-[#06513D] font-light px-4 py-5 hover:!bg-[#F8F8F8] hover:!text-[#06513D]"
            >
              Import Department
            </Button>

            <button
              className="bg-[#06513D] rounded-md hover:bg-[#06513DCC] text-[12px] text-white font-light px-4 py-3 w-full sm:w-auto"
              onClick={() => setAddDepartments(true)}
            >
              + Add Department
            </button>
          </div>
        </div>

        {/* Table */}
        <Tables
          data={tableData}
          headers={headers}
          onChange={handlePageChange}
          pageSize={pageSize}
          current={currentPage}
          total={filteredDepartmentList.length}
        />
      </div>

      {/* Link Location Popup */}
      {openLinkPopup && (
        <LinkLocationPopup
          onOpen={openLinkPopup}
          onClose={() => {
            setOpenLinkPopup(false);
            setSelectedDeptForLink(null);
          }}
          departmentName={selectedDeptForLink?.name}
          departmentId={selectedDeptForLink?._id}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmation.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="warning"
            title="Confirm Delete"
            message={`Are you sure you want to delete ${deleteConfirmation.departmentName}?`}
            showActions={true}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onCancel={() =>
              setDeleteConfirmation({
                visible: false,
                departmentName: "",
              })
            }
            onConfirm={() => {
              dispatch(deleteDepartment(deleteConfirmation.departmentId))
                .unwrap()
                .then((res) => {
                  setDeleteConfirmation({
                    visible: false,
                    departmentId: null,
                    departmentName: "",
                  });

                  setSuccessPopup({
                    visible: true,
                    title: "Deleted",
                    message: res?.message || "Department deleted successfully!",
                  });

                  setTimeout(() => {
                    setSuccessPopup({
                      visible: false,
                      title: "",
                      message: "",
                    });
                  }, 3000);
                })
                .catch((err) => {
                  setDeleteConfirmation({
                    visible: false,
                    departmentId: null,
                    departmentName: "",
                  });
                  setSuccessPopup({
                    visible: true,
                    title: "Error",
                    message:
                      typeof err === "string"
                        ? err
                        : err?.message || "Failed to delete department.",
                  });
                  setTimeout(() => {
                    setSuccessPopup({
                      visible: false,
                      title: "",
                      message: "",
                    });
                  }, 3000);
                });
            }}
          />
        </div>
      )}
      {/* Success Popup */}
      {successPopup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="success"
            title={successPopup.title}
            message={successPopup.message}
          />
        </div>
      )}
      {/* -----Add Department---- */}
      {addDepartments && (
        <AddDepartment
          onOpen={addDepartments}
          onClose={() => {
            setAddDepartments(false);
          }}
        />
      )}
      {/* ----------Edit Department------- */}
      {openEditBox && (
        <EditDepartment
          onOpen={openEditBox}
          onClose={() => {
            setOpenEditBox(false);
          }}
        />
      )}
      {/* -----------Filter---------- */}
      {openFilter && (
        <FilterLocationDeparment
          onOpen={openFilter}
          onClose={() => setOpenFilter(false)}
          currentStatus={statusFilter}
          onApply={(status) => {
            setStatusFilter(status);
            setCurrentPage(1); // Reset to first page on filter application
          }}
        />
      )}
    </div>
  );
};

export default Department;
